import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import routes, { smeBaseName } from "../../routes";
import {
  StatusContainer,
  StatusContainerNoIcon,
  MessageContainer,
  LinkContainer,
  StyledLink,
  StatusText,
  StatusTextNoIcon
} from "./styled";
import { UploadLimitComponent } from "../../containers/UploadDocuments/components/UploadLimit/UploadLimit";
import { DECLINE, INELIGIBLE } from "./constants";
import { FailedStatus } from "./components/FailedStatus";
import { DeclinedStatus } from "./components/DeclinedStatus";
import {
  getCompanyName,
  getFinanceType,
  getApplicationStatus
} from "../../store/selectors/appConfig";
import { resetScreeningError } from "../../store/actions/sendProspectToAPI";
import { resetProspect } from "../../store/actions/appConfig";

const financeComparisonRoute = {
  link: routes.financeProductComparison,
  label: "See products"
};

/**
 * ApplicationStatus Component
 * Displays different status screens based on application status
 *
 * @component
 */
export const ApplicationStatus = ({
  icon,
  text,
  link,
  screeningType = "",
  screeningReason = "",
  buttons = [financeComparisonRoute]
}) => {
  const companyName = useSelector(getCompanyName);
  const financeType = useSelector(getFinanceType);
  const applicationStatus = useSelector(getApplicationStatus);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handlePopState = () => {
      dispatch(resetScreeningError());
      dispatch(resetProspect());
      navigate(smeBaseName);
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (applicationStatus === INELIGIBLE) {
    return <FailedStatus companyName={companyName} financeType={financeType} />;
  }

  if (applicationStatus === DECLINE) {
    return (
      <DeclinedStatus icon={icon} financeType={financeType} screeningReason={screeningReason} />
    );
  }

  if (screeningType === "Total No of Documents uploaded check") {
    return <UploadLimitComponent />;
  }

  if (icon) {
    return (
      <StatusContainer>
        <img src={icon} alt="Status" />
        <MessageContainer>
          <StatusText>{text}</StatusText>
          {link && (
            <LinkContainer>
              {buttons.map((button, index) => (
                <StyledLink key={`btn-${button.link}`} component={RouterLink} to={button.link}>
                  {button.label}
                </StyledLink>
              ))}
            </LinkContainer>
          )}
        </MessageContainer>
      </StatusContainer>
    );
  }

  return (
    <StatusContainerNoIcon>
      <MessageContainer>
        <StatusTextNoIcon>{text}</StatusTextNoIcon>
        {link && (
          <LinkContainer>
            {buttons.map((button, index) => (
              <StyledLink key={`btn-${button.link}`} component={RouterLink} to={button.link}>
                {button.label}
              </StyledLink>
            ))}
          </LinkContainer>
        )}
      </MessageContainer>
    </StatusContainerNoIcon>
  );
};

ApplicationStatus.propTypes = {
  /** Icon to display above the status message */
  icon: PropTypes.string,
  /** Text message to display as status */
  text: PropTypes.string.isRequired,
  /** Flag to indicate if links should be shown */
  link: PropTypes.bool,
  /** Type of screening being performed */
  screeningType: PropTypes.string,
  /** Reason for screening outcome */
  screeningReason: PropTypes.string,
  /** Array of button configurations with link and label */
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  )
};

export default ApplicationStatus;
